import React from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { Linkedin } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  API,
  COMPANY_LINKEDIN,
  easeOutExpo,
  FOUNDER_LINKEDIN,
  INQUIRY_OPTIONS,
} from "./shared";

export const ContactForm = () => {
  const [form, setForm] = React.useState({
    name: "",
    email: "",
    organization: "",
    inquiry_type: "",
    message: "",
  });
  const [status, setStatus] = React.useState("idle"); // idle | submitting | success | error
  const [errorMsg, setErrorMsg] = React.useState("");

  const update = (key) => (e) =>
    setForm((f) => ({ ...f, [key]: e?.target ? e.target.value : e }));

  const onSubmit = async (e) => {
    e.preventDefault();
    setErrorMsg("");
    if (
      !form.name.trim() ||
      !form.email.trim() ||
      !form.inquiry_type ||
      !form.message.trim()
    ) {
      setErrorMsg("Please complete the required fields.");
      return;
    }
    setStatus("submitting");
    try {
      await axios.post(`${API}/contact`, {
        name: form.name.trim(),
        email: form.email.trim(),
        organization: form.organization.trim() || null,
        inquiry_type: form.inquiry_type,
        message: form.message.trim(),
      });
      setStatus("success");
    } catch (err) {
      setStatus("error");
      setErrorMsg(
        err?.response?.data?.detail
          ? typeof err.response.data.detail === "string"
            ? err.response.data.detail
            : "Please check the form and try again."
          : "Something went wrong. Please try again or email us directly."
      );
    }
  };

  if (status === "success") {
    return (
      <motion.div
        initial={{ opacity: 0, y: 24 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 1.0, ease: easeOutExpo }}
        className="mx-auto max-w-xl text-center"
        data-testid="contact-success"
      >
        <h3 className="font-serif text-white text-3xl md:text-4xl font-light leading-tight">
          Thank you.
        </h3>
        <p className="mt-5 md:mt-6 text-white/90 font-sans font-light text-base md:text-lg leading-relaxed">
          We&apos;ll be in touch within 5 business days.
        </p>
      </motion.div>
    );
  }

  const fieldBase =
    "w-full bg-white/10 border border-white/25 text-white placeholder:text-white/55 font-sans font-light focus-visible:ring-0 focus-visible:border-white/70 backdrop-blur-sm transition-colors duration-300";

  return (
    <form
      onSubmit={onSubmit}
      noValidate
      className="mx-auto w-full max-w-2xl text-left"
      data-testid="contact-form"
    >
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Input
          type="text"
          required
          placeholder="Full name *"
          value={form.name}
          onChange={update("name")}
          data-testid="contact-name"
          className={`${fieldBase} h-12 px-4`}
        />
        <Input
          type="email"
          required
          placeholder="Email *"
          value={form.email}
          onChange={update("email")}
          data-testid="contact-email"
          className={`${fieldBase} h-12 px-4`}
        />
      </div>

      <Input
        type="text"
        placeholder="Company / Organisation"
        value={form.organization}
        onChange={update("organization")}
        data-testid="contact-organization"
        className={`${fieldBase} h-12 px-4 mt-4`}
      />

      <div className="mt-4">
        <Select
          value={form.inquiry_type}
          onValueChange={(v) => setForm((f) => ({ ...f, inquiry_type: v }))}
        >
          <SelectTrigger
            data-testid="contact-inquiry-type"
            className={`${fieldBase} h-12 px-4 data-[placeholder]:text-white/55`}
          >
            <SelectValue placeholder="I am a... *" />
          </SelectTrigger>
          <SelectContent
            className="bg-[#101a13] text-white border-white/20 font-sans"
            data-testid="contact-inquiry-content"
          >
            {INQUIRY_OPTIONS.map((opt) => (
              <SelectItem
                key={opt.value}
                value={opt.value}
                className="cursor-pointer focus:bg-white/10 focus:text-white"
                data-testid={`contact-inquiry-option-${opt.value}`}
              >
                {opt.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      <Textarea
        required
        placeholder="Your message *"
        value={form.message}
        onChange={update("message")}
        rows={3}
        data-testid="contact-message"
        className={`${fieldBase} px-4 py-3 mt-4 resize-none`}
      />

      {errorMsg && (
        <p
          className="mt-4 text-sm text-rose-200 font-sans"
          data-testid="contact-error"
        >
          {errorMsg}
        </p>
      )}

      <div className="mt-7 flex flex-col sm:flex-row items-center justify-between gap-5">
        <button
          type="submit"
          disabled={status === "submitting"}
          data-testid="contact-submit"
          className="group inline-flex items-center gap-2 px-8 py-3.5 text-sm tracking-wide font-sans font-semibold bg-[#FAF8F3] text-[#1A1A1A] transition-all duration-500 hover:bg-white hover:translate-x-1 disabled:opacity-60 disabled:cursor-not-allowed disabled:hover:translate-x-0"
        >
          {status === "submitting" ? "Sending…" : "Send message"}
          <span
            aria-hidden="true"
            className="text-base leading-none transition-transform duration-500 group-hover:translate-x-1"
          >
            →
          </span>
        </button>

        <div className="flex items-center gap-4 text-white/75">
          <a
            href={COMPANY_LINKEDIN}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="MatiMitra on LinkedIn"
            data-testid="linkedin-company"
            className="flex items-center gap-2 hover:text-white transition-colors duration-300 text-[12px] tracking-[0.16em] uppercase font-sans font-semibold"
          >
            <Linkedin className="w-4 h-4" /> Company
          </a>
          <span aria-hidden="true" className="text-white/30">·</span>
          <a
            href={FOUNDER_LINKEDIN}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Akash Bisoyi on LinkedIn"
            data-testid="linkedin-founder"
            className="flex items-center gap-2 hover:text-white transition-colors duration-300 text-[12px] tracking-[0.16em] uppercase font-sans font-semibold"
          >
            <Linkedin className="w-4 h-4" /> Founder
          </a>
        </div>
      </div>
    </form>
  );
};
